import { coerceBooleanProperty, type BooleanInput } from '@angular/cdk/coercion';
import {
  Directive,
  ElementRef,
  inject,
  Input,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { THEME_TOKEN } from '@porsche-design-system/components-angular';
import { tap } from 'rxjs';
import { SubSink } from 'subsink';
import tippy, { type Instance, type Placement, type Props } from 'tippy.js';
import type { NgChanges } from '../models/ng-changes.model';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[tippy]',
  standalone: true,
})
export class TippyDirective implements OnChanges, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly theme = inject(THEME_TOKEN);

  @Input() tippy: string | null | undefined;
  @Input() tippyDisabled: BooleanInput;
  @Input() tippyPlacement: Placement | null | undefined;

  private readonly defaultPlacement: Placement = 'bottom';
  private tooltip: Instance<Props> | undefined;
  private readonly subSink = new SubSink();

  constructor() {
    this.subSink.sink = this.theme
      .pipe(tap((theme) => this.tooltip?.setProps({ theme: `theme-${theme}` })))
      .subscribe();
  }

  ngOnChanges(changes: NgChanges<TippyDirective>): void {
    if (!this.tooltip)
      this.tooltip = tippy(this.elementRef.nativeElement, {
        placement: this.defaultPlacement,
        animation: false,
        offset: [0, 16], // adjust based on arrow size (in css)
        theme: `theme-${this.theme.value}`,
      });

    if (changes.tippy) this.tooltip.setContent(this.tippy ?? '');

    if (changes.tippy || changes.tippyDisabled) {
      const disabled = !this.tippy || coerceBooleanProperty(this.tippyDisabled);
      if (disabled) this.tooltip.disable();
      else this.tooltip.enable();
    }

    if (changes.tippyPlacement)
      this.tooltip.setProps({ placement: this.tippyPlacement ?? this.defaultPlacement });
  }

  ngOnDestroy(): void {
    this.tooltip?.destroy();
    this.subSink.unsubscribe();
  }
}
